import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Maybe } from 'types/UtilityTypes';

dayjs.extend(utc);

const SENTINEL = '0001-01-01T00:00:00Z';

function isValidYmdHms(
  y: number,
  mo: number,
  dom: number,
  h: number,
  mi: number,
  s: number
): boolean {
  if (mo < 1 || mo > 12 || dom < 1 || dom > 31) return false;
  if (h > 23 || mi > 59 || s > 59) return false;
  const t = Date.UTC(y, mo - 1, dom, h, mi, s);
  const check = new Date(t);
  return (
    check.getUTCFullYear() === y &&
    check.getUTCMonth() === mo - 1 &&
    check.getUTCDate() === dom
  );
}

/**
 * Interprets date/time as UTC wall-clock: the literal calendar digits from the
 * value are stored as that moment in UTC (not the same instant as ISO parsing).
 *
 * @example parseToUtcWallClockDate('2025-03-15T15:00:00-05:00') → Mar 15 2025 15:00 UTC
 * @example parseToUtcWallClockDate('2025-03-15') → Mar 15 2025 00:00 UTC
 */
export function parseToUtcWallClockDate(value: unknown): Maybe<Date> {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s || s === SENTINEL) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, mo, dom] = s.split('-').map(Number);
      if (!isValidYmdHms(y, mo, dom, 0, 0, 0)) return null;
      const d = dayjs.utc(`${s} 00:00:00.000`, 'YYYY-MM-DD HH:mm:ss.SSS');
      return d.isValid() ? d.toDate() : null;
    }

    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?/
    );
    if (!m) {
      return null;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const dom = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = m[6] != null ? Number(m[6]) : 0;
    const frac = m[7] ?? '';
    const msec = frac ? Number(frac.padEnd(3, '0').slice(0, 3)) : 0;
    if (
      Number.isNaN(y) ||
      Number.isNaN(mo) ||
      Number.isNaN(dom) ||
      Number.isNaN(hh) ||
      Number.isNaN(mm) ||
      Number.isNaN(ss) ||
      Number.isNaN(msec) ||
      msec > 999 ||
      !isValidYmdHms(y, mo, dom, hh, mm, ss)
    ) {
      return null;
    }
    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    const d = dayjs.utc(
      `${y}-${pad(mo)}-${pad(dom)} ${pad(hh)}:${pad(mm)}:${pad(ss)}.${String(msec).padStart(3, '0')}`,
      'YYYY-MM-DD HH:mm:ss.SSS'
    );
    return d.isValid() ? d.toDate() : null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    const y = value.getUTCFullYear();
    const mo = value.getUTCMonth() + 1;
    const dom = value.getUTCDate();
    const h = value.getUTCHours();
    const mi = value.getUTCMinutes();
    const sec = value.getUTCSeconds();
    const ms = value.getUTCMilliseconds();
    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    const parsed = dayjs.utc(
      `${y}-${pad(mo)}-${pad(dom)} ${pad(h)}:${pad(mi)}:${pad(sec)}.${String(ms).padStart(3, '0')}`,
      'YYYY-MM-DD HH:mm:ss.SSS'
    );
    return parsed.isValid() ? parsed.toDate() : null;
  }

  return null;
}
